/**
 * Cliente WebSocket.
 *
 * Reconecta com backoff e reassina os projetos ao voltar — uma conexão caída durante
 * um render de 40 minutos não pode custar o acompanhamento do progresso.
 */
import { accessToken } from '../api/client'

type Handler = (event: Record<string, unknown>) => void

const handlers = new Map<string, Set<Handler>>()
const subscriptions = new Set<string>()

let socket: WebSocket | null = null
let reconnectAttempt = 0
let reconnectTimer: number | null = null
let heartbeat: number | null = null

export function connect(): void {
  const token = accessToken()
  if (!token) return
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  socket = new WebSocket(`${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`)

  socket.addEventListener('open', () => {
    reconnectAttempt = 0
    for (const projectId of subscriptions) {
      socket?.send(JSON.stringify({ type: 'subscribe', projectId }))
    }
    // Ping periódico: proxies costumam derrubar conexões ociosas em 60s.
    heartbeat = window.setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'ping' }))
    }, 30_000)
  })

  socket.addEventListener('message', (message) => {
    let payload: Record<string, unknown>
    try {
      payload = JSON.parse(message.data as string) as Record<string, unknown>
    } catch {
      return
    }
    const name = payload.event as string | undefined
    if (!name) return
    handlers.get(name)?.forEach((fn) => fn(payload))
    handlers.get('*')?.forEach((fn) => fn(payload))
  })

  socket.addEventListener('close', () => {
    if (heartbeat) window.clearInterval(heartbeat)
    heartbeat = null
    socket = null
    scheduleReconnect()
  })

  socket.addEventListener('error', () => socket?.close())
}

function scheduleReconnect(): void {
  if (reconnectTimer) return
  // Backoff limitado a 15s: reconectar em rajada não ajuda e sobrecarrega o servidor.
  const delay = Math.min(15_000, 500 * 2 ** reconnectAttempt)
  reconnectAttempt++
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null
    connect()
  }, delay)
}

export function subscribeProject(projectId: string): void {
  subscriptions.add(projectId)
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: 'subscribe', projectId }))
  } else {
    connect()
  }
}

export function unsubscribeProject(projectId: string): void {
  subscriptions.delete(projectId)
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: 'unsubscribe', projectId }))
  }
}

export function on(event: string, handler: Handler): () => void {
  let set = handlers.get(event)
  if (!set) {
    set = new Set()
    handlers.set(event, set)
  }
  set.add(handler)
  return () => set!.delete(handler)
}

export function disconnect(): void {
  subscriptions.clear()
  handlers.clear()
  if (reconnectTimer) window.clearTimeout(reconnectTimer)
  socket?.close()
  socket = null
}
